/**
 * PremiumSheet.js
 *
 * The one bottom-sheet shell of the management surfaces ("Happy Premium"
 * language): SOLID `surface` pane (never glass / blur) over a dim scrim, grabber,
 * header with a soft tinted {@link SheetIconChip} + title + round close button,
 * scrolling body and a sticky footer with an outlined cancel pill next to an
 * orange primary pill (white text).
 *
 * Presentation only — callers own state and handlers. Also exports the small
 * form atoms the sheets share: {@link SheetLabel}, {@link SheetChip},
 * {@link SheetTile} and {@link SheetInput}.
 *
 * NOTE: deliberately free of `BRAND` / module-scope theme access so that suites
 * which mock `helpers/managementTheme` (`useManagementTheme` only) can still
 * import the screens that mount these sheets.
 *
 * @module components/management/PremiumSheet
 */

import React, {useCallback, useState} from 'react';
import {ActivityIndicator, KeyboardAvoidingView, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View} from 'react-native';
import {modalKavBehavior} from '../KeyboardAware';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import Icon from './SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';

/**
 * Soft tinted icon chip for sheet headers (tone colour at ~12 % alpha).
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} [props.tone='primary'] Tone key (primary/success/info/violet/danger/warning/neutral).
 * @param {number} [props.size=44] Chip edge in px.
 * @returns {JSX.Element} The chip.
 */
export const SheetIconChip = ({icon, tone = 'primary', size = 44}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.iconChip, {width: size, height: size, borderRadius: Math.round(size * 0.34), backgroundColor: m.toneTint(tone)}]}>
            <Icon name={icon} size={Math.round(size * 0.5)} color={m.toneColor(tone)} />
        </View>
    );
};

/**
 * Premium bottom sheet.
 *
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {() => void} props.onClose Dismiss handler (scrim tap, close button, Android back).
 * @param {string} props.title Sheet title.
 * @param {string} [props.subtitle] Muted line under the title.
 * @param {string} [props.icon] MDI glyph of the header chip.
 * @param {string} [props.tone='primary'] Tone of the header chip.
 * @param {boolean} [props.busy=false] Blocks dismissal / cancel and spins the primary while a request is in flight.
 * @param {React.ReactNode} [props.headerExtra] Fixed block between the header and the scrolling body (tab row, lens chips…).
 * @param {React.ReactNode} props.children Body content.
 * @param {boolean} [props.scroll=true] Wrap the body in a ScrollView.
 * @param {string} [props.maxHeight='92%'] Sheet height cap.
 * @param {boolean} [props.keyboardAware=true] Lift the sheet above the keyboard on iOS.
 * @param {React.ReactNode} [props.overlay] Rendered inside the Modal after the sheet (nested pickers).
 * @param {boolean} [props.hideFooter=false] No footer at all (pick-one menus).
 * @param {React.ReactNode} [props.footer] Custom footer content replacing the two default pills.
 * @param {string} [props.cancelLabel] Outlined cancel label (default: the shared "cancel" string).
 * @param {string} [props.cancelIcon] Cancel glyph.
 * @param {() => void} [props.onCancel] Cancel handler (default: dismiss).
 * @param {boolean} [props.cancelDisabled=false] Disable the cancel pill.
 * @param {string} [props.confirmLabel] Primary pill label (omit to render the cancel pill only).
 * @param {string} [props.confirmIcon] Primary glyph.
 * @param {() => void} [props.onConfirm] Primary handler.
 * @param {boolean} [props.confirmDisabled=false] Disable the primary pill.
 * @param {boolean} [props.confirmLoading] Spinner on the primary (defaults to `busy`).
 * @param {boolean} [props.destructive=false] Danger-tone primary.
 * @returns {JSX.Element} The sheet.
 */
const PremiumSheet = ({
    visible,
    onClose,
    title,
    subtitle,
    icon = 'text-box-outline',
    tone = 'primary',
    busy = false,
    headerExtra,
    children,
    scroll = true,
    maxHeight = '92%',
    keyboardAware = true,
    overlay,
    hideFooter = false,
    footer,
    cancelLabel,
    cancelIcon,
    onCancel,
    cancelDisabled = false,
    confirmLabel,
    confirmIcon,
    onConfirm,
    confirmDisabled = false,
    confirmLoading,
    destructive = false,
}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const insets = useSafeAreaInsets();
    const loading = confirmLoading ?? busy;
    const Shell = keyboardAware ? KeyboardAvoidingView : View;
    // A RN <Modal> is its own window and does not inherit the activity's adjustResize, so
    // Android must avoid the keyboard explicitly ('height'); iOS pads (see KeyboardAware).
    const shellProps = keyboardAware ? {behavior: modalKavBehavior} : {};
    const primaryColor = destructive ? m.colors.danger : m.colors.primary;

    const dismiss = useCallback(() => {
        if (!busy) {
            onClose?.();
        }
    }, [busy, onClose]);

    return (
        <Modal visible={!!visible} transparent animationType="slide" onRequestClose={dismiss}>
            <Shell style={styles.backdrop} {...shellProps}>
                <Pressable style={styles.backdropFill} onPress={dismiss} />
                {/* Solid (never glassy) pane — modals may not be translucent. */}
                <View style={[styles.sheet, {backgroundColor: m.colors.surface, borderColor: m.colors.border, maxHeight, paddingBottom: Math.max(insets.bottom, 12)}]}>
                    <View style={styles.grabber}><View style={[styles.grabberBar, {backgroundColor: m.colors.border}]} /></View>

                    <View style={styles.header}>
                        <SheetIconChip icon={icon} tone={tone} />
                        <View style={styles.headerText}>
                            <Text style={[styles.title, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                            {!!subtitle && <Text style={[styles.subtitle, {color: m.colors.subText}]} numberOfLines={2}>{subtitle}</Text>}
                        </View>
                        <TouchableOpacity
                            onPress={dismiss}
                            hitSlop={10}
                            disabled={busy}
                            accessibilityRole="button"
                            style={[styles.closeBtn, {backgroundColor: m.colors.surfaceAlt, opacity: busy ? 0.4 : 1}]}
                        >
                            <Icon name="close" size={18} color={m.colors.subText} />
                        </TouchableOpacity>
                    </View>

                    {headerExtra}

                    {scroll ? (
                        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.body} keyboardShouldPersistTaps="handled">
                            {children}
                        </ScrollView>
                    ) : (
                        <View style={styles.body}>{children}</View>
                    )}

                    {!hideFooter && (
                        <View style={[styles.footer, {borderTopColor: m.colors.border}]}>
                            {footer || (
                                <>
                                    <TouchableOpacity
                                        activeOpacity={0.85}
                                        disabled={busy || cancelDisabled}
                                        onPress={onCancel || dismiss}
                                        accessibilityRole="button"
                                        style={[styles.footerBtn, {borderWidth: 1.5, borderColor: m.colors.border, opacity: busy || cancelDisabled ? 0.5 : 1}]}
                                    >
                                        {!!cancelIcon && <Icon name={cancelIcon} size={18} color={m.colors.subText} />}
                                        <Text style={[styles.footerBtnText, {color: m.colors.text}]} numberOfLines={1}>
                                            {cancelLabel || t('cancel', {defaultValue: 'Mégsem'})}
                                        </Text>
                                    </TouchableOpacity>
                                    {!!confirmLabel && (
                                        <TouchableOpacity
                                            activeOpacity={0.85}
                                            disabled={loading || confirmDisabled}
                                            onPress={onConfirm}
                                            accessibilityRole="button"
                                            style={[
                                                styles.footerBtn,
                                                styles.footerPrimary,
                                                {backgroundColor: primaryColor, shadowColor: primaryColor, opacity: loading || confirmDisabled ? 0.6 : 1},
                                            ]}
                                        >
                                            {loading ? <ActivityIndicator color="#FFFFFF" /> : (
                                                <>
                                                    {!!confirmIcon && <Icon name={confirmIcon} size={18} color="#FFFFFF" />}
                                                    <Text style={[styles.footerBtnText, {color: '#FFFFFF'}]} numberOfLines={1}>{confirmLabel}</Text>
                                                </>
                                            )}
                                        </TouchableOpacity>
                                    )}
                                </>
                            )}
                        </View>
                    )}
                </View>
            </Shell>
            {overlay}
        </Modal>
    );
};

/**
 * Uppercase group label above a block of sheet fields.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Label text.
 * @param {object} [props.style] Extra style (margins, colour override).
 * @returns {JSX.Element} The label.
 */
export const SheetLabel = ({children, style}) => {
    const m = useManagementTheme();

    return <Text style={[styles.label, {color: m.colors.subText}, style]}>{children}</Text>;
};

/**
 * Pill chip: filled neutral when idle, tinted + coloured border when active.
 *
 * @param {object} props
 * @param {string} props.label Chip text.
 * @param {boolean} [props.active=false] Selected state.
 * @param {() => void} [props.onPress] Tap handler.
 * @param {string} [props.icon] Leading glyph.
 * @param {string} [props.color] Accent colour (`#rrggbb`, default brand orange).
 * @param {boolean} [props.disabled=false] Disabled state.
 * @param {number} [props.maxLines=1] Label line cap.
 * @returns {JSX.Element} The chip.
 */
export const SheetChip = ({label, active = false, onPress, icon, color, disabled = false, maxLines = 1}) => {
    const m = useManagementTheme();
    const accent = color || m.colors.primary;
    const tint = /^#[0-9A-Fa-f]{6}$/.test(accent) ? `${accent}${m.isDark ? '33' : '1F'}` : m.colors.chipBg;

    return (
        <TouchableOpacity
            activeOpacity={0.85}
            disabled={disabled}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityState={{selected: active, disabled}}
            style={[
                styles.chip,
                {borderColor: active ? accent : m.colors.border, backgroundColor: active ? tint : m.colors.surfaceAlt, opacity: disabled ? 0.55 : 1},
            ]}
        >
            {!!icon && <Icon name={icon} size={14} color={active ? accent : m.colors.subText} />}
            <Text style={[styles.chipText, {color: active ? accent : m.colors.subText}]} numberOfLines={maxLines}>{label}</Text>
        </TouchableOpacity>
    );
};

/**
 * Icon-over-label selection tile (channel / follow-up type pickers).
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} props.label Tile text.
 * @param {boolean} [props.active=false] Selected state.
 * @param {() => void} [props.onPress] Tap handler.
 * @param {number} [props.minWidth=72] Minimum tile width.
 * @returns {JSX.Element} The tile.
 */
export const SheetTile = ({icon, label, active = false, onPress, minWidth = 72}) => {
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            activeOpacity={0.85}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityState={{selected: active}}
            style={[
                styles.tile,
                {minWidth, borderColor: active ? m.colors.primary : m.colors.border, backgroundColor: active ? m.colors.chipBg : m.colors.surfaceAlt},
            ]}
        >
            <Icon name={icon} size={22} color={active ? m.colors.primary : m.colors.subText} />
            <Text style={[styles.tileText, {color: active ? m.colors.primary : m.colors.subText}]} numberOfLines={1}>{label}</Text>
        </TouchableOpacity>
    );
};

/**
 * Premium text input (rounded, filled, orange focus ring) for sheets that need
 * a bare input next to their own label (otherwise use `EditableField`).
 *
 * @param {object} props TextInput props; `style` is merged last.
 * @returns {JSX.Element} The input.
 */
export const SheetInput = ({style, multiline, onFocus, onBlur, ...rest}) => {
    const m = useManagementTheme();
    const [focused, setFocused] = useState(false);

    return (
        <TextInput
            placeholderTextColor={m.colors.placeholder}
            multiline={multiline}
            {...rest}
            onFocus={(event) => { setFocused(true); onFocus?.(event); }}
            onBlur={(event) => { setFocused(false); onBlur?.(event); }}
            style={[
                styles.input,
                multiline && styles.inputMultiline,
                {color: m.colors.heading, backgroundColor: m.colors.inputBg, borderColor: focused ? m.colors.primary : m.colors.inputBorder},
                style,
            ]}
        />
    );
};

const styles = StyleSheet.create({
    iconChip: {alignItems: 'center', justifyContent: 'center'},

    backdrop: {flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', justifyContent: 'flex-end'},
    backdropFill: {...StyleSheet.absoluteFillObject},
    sheet: {borderTopLeftRadius: 28, borderTopRightRadius: 28, borderWidth: StyleSheet.hairlineWidth},
    grabber: {alignItems: 'center', paddingTop: 10, paddingBottom: 2},
    grabberBar: {width: 44, height: 5, borderRadius: 999},

    header: {flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 20, paddingTop: 10, paddingBottom: 12},
    headerText: {flex: 1, minWidth: 0},
    title: {fontSize: 19, fontWeight: '800', letterSpacing: -0.3},
    subtitle: {fontSize: 12.5, fontWeight: '600', marginTop: 2},
    closeBtn: {width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center'},

    body: {paddingHorizontal: 20, paddingBottom: 16},

    footer: {flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingTop: 12, borderTopWidth: StyleSheet.hairlineWidth},
    footerBtn: {flex: 1, minHeight: 52, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 999, paddingHorizontal: 16},
    footerPrimary: {flex: 1.6, shadowOffset: {width: 0, height: 8}, shadowOpacity: 0.28, shadowRadius: 14, elevation: 5},
    footerBtnText: {fontSize: 15, fontWeight: '800'},

    label: {fontSize: 11.5, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, marginTop: 4},

    chip: {flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, borderWidth: 1.2, paddingHorizontal: 13, paddingVertical: 8},
    chipText: {fontSize: 12.5, fontWeight: '800', flexShrink: 1},

    tile: {flexGrow: 1, alignItems: 'center', gap: 5, borderRadius: 18, borderWidth: 1.2, paddingVertical: 12, paddingHorizontal: 8},
    tileText: {fontSize: 11.5, fontWeight: '800'},

    input: {borderRadius: 16, borderWidth: 1.2, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, fontWeight: '600'},
    inputMultiline: {minHeight: 92, textAlignVertical: 'top'},
});

export default PremiumSheet;
